import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { RadioGroupField } from "$components/rhf-fields";
import { Box, makeStyles, styled, Typography } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles((theme) => ({
  warningBreakout: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    alignItems: "center",
    borderRadius: "8px",
  },
  warningIcon: {
    color: theme.palette.warning.main,
    height: 50,
    width: 50,
  },
  radioContainer: {
    display: "flex",
    flexWrap: "nowrap",
    flexDirection: "column",
  },
}));

const LabelWithMarginTop = styled(Typography)(() => ({
  marginTop: "12px",
}));

export const ConsentRHFModal: FC<{
  open?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  submitLoading: boolean;
  fieldNames: {
    healthcareComms: string;
    elephantUseOfAnonData: string;
    productMarketing: string;
  };
  dob: string;
}> = ({ open = false, onClose, onSubmit, submitLoading, fieldNames, dob }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const isUnderAge = isDOBUnderAge(dob);

  return (
    <DialogContainer
      open={open}
      onClose={onClose}
      data-testid="ConsentDialog"
      style={{ overflowY: "scroll" }}
    >
      <DialogHeader title="">
        <Typography variant="h4" style={{ marginBottom: 0 }}>
          <FormattedMessage
            id="patient.consent.marketing.title"
            defaultMessage="Marketing Consent"
          />
        </Typography>
      </DialogHeader>
      <DialogBody>
        <Box p={2} display="flex" mb={2} className={classes.warningBreakout}>
          <Box mr={2}>
            <ErrorOutlineIcon className={classes.warningIcon} />
          </Box>
          <Typography className={classes.warningBreakout}>
            {isUnderAge
              ? formatMessage({
                  id: "registration.consent.child.warning",
                  defaultMessage:
                    "Last step. You need to ask the patient's parent or guardian three questions about how their child’s data can be used.",
                })
              : formatMessage({
                  id: "registration.consent.adult.warning",
                  defaultMessage:
                    "Last step. You need to ask the patient three questions about how their data can be used.",
                })}
          </Typography>
        </Box>
        <div>
          <Typography variant="body1" paragraph>
            {isUnderAge
              ? formatMessage({
                  id: "registration.consent.child.healthcareComms.title",
                  defaultMessage:
                    "Do you give permission for Elephant to contact you via SMS and WhatsApp about your child's healthcare?",
                })
              : formatMessage({
                  id: "registration.consent.adult.healthcareComms.title",
                  defaultMessage:
                    "Do you give permission for Elephant to contact you via SMS and WhatsApp about your healthcare?",
                })}
          </Typography>
          <Typography variant="body2" paragraph>
            {formatMessage({
              id: "registration.consent.healthcareComms.description",
              defaultMessage:
                "This includes appointment reminders, treatment updates, and other medical-related information and services.",
            })}
          </Typography>
          <RadioGroupField
            resetMarginTop
            name={fieldNames.healthcareComms}
            required
            data-testid="consent.healthcareComms"
            options={[
              {
                label: "Yes",
                value: true,
                "data-testid": "consent.healthcareComms.yes",
              },
              {
                label: "No",
                value: false,
                "data-testid": "consent.healthcareComms.no",
              },
            ]}
          />
        </div>
        <div>
          <LabelWithMarginTop variant="body1" paragraph>
            {isUnderAge
              ? formatMessage({
                  id: "registration.consent.child.productMarketing",
                  defaultMessage:
                    "Elephant would like to contact you on behalf of your child with relevant product and feature updates that it thinks will be helpful for you. You can change your mind at any time by visiting the web link on your card.",
                })
              : formatMessage({
                  id: "registration.consent.adult.productMarketing",
                  defaultMessage:
                    "Do you give permission for Elephant to contact you about its products, news and events?",
                })}
          </LabelWithMarginTop>
          <RadioGroupField
            resetMarginTop
            name={fieldNames.productMarketing}
            required
            options={[
              {
                label: "Yes",
                value: true,
                "data-testid": "consent.productMarketing.yes",
              },
              {
                label: "No",
                value: false,
                "data-testid": "consent.productMarketing.no",
              },
            ]}
          />
        </div>
        <div>
          <LabelWithMarginTop variant="body1" paragraph>
            {isUnderAge
              ? formatMessage({
                  id: "registration.consent.child.elephantUseOfAnonData",
                  defaultMessage:
                    "Do you give permission for Elephant to anonymise your child’s data so they can use it to improve healthcare?",
                })
              : formatMessage({
                  id: "registration.consent.adult.elephantUseOfAnonData",
                  defaultMessage:
                    "Do you give permission for Elephant to anonymise your data so they can use it to improve healthcare?",
                })}
          </LabelWithMarginTop>
          <RadioGroupField
            resetMarginTop
            name={fieldNames.elephantUseOfAnonData}
            required
            options={[
              {
                label: "Yes",
                value: true,
                "data-testid": "consent.elephantUseOfAnonData.yes",
              },
              {
                label: "No",
                value: false,
                "data-testid": "consent.elephantUseOfAnonData.no",
              },
            ]}
          />
        </div>
        <Typography variant="body2">
          {formatMessage({
            id: "registration.consent.changeInfoLabel",
            defaultMessage:
              "You can change your answers at any time by contacting Elephant through their website or we can message them for you.",
          })}
        </Typography>
      </DialogBody>
      <DialogFooter>
        <Button
          color="secondary"
          onClick={() => {
            onClose();
          }}
        >
          <FormattedMessage
            id="registration.consent.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          type="submit"
          onClick={() => {
            onSubmit();
          }}
          loading={submitLoading}
        >
          <FormattedMessage
            id="registration.consent.confirm"
            defaultMessage="Accept & Continue"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};

export const isDOBUnderAge = (dob, { ageOfConsent = 18, now = dayjs() } = {}) =>
  dayjs(now).diff(dob, "year") < ageOfConsent;
